<template>
  <module-page v-bind="pageParams">
    <screen-loading v-if="loading"></screen-loading>
    <div v-else-if="profileError" class="empty-state">
      {{ $t('responses.error') }}
    </div>
    <v-form
      v-else
      @submit.prevent="submit"
      ref="profileForm"
      v-model="formValid"
      lazy-validation
    >
      <v-layout row wrap>
        <v-flex xs12 md8 lg8>
          <v-card>
            <v-card-title class="pt-headline profile--color">{{
              $t('pages.profile.personalData')
            }}</v-card-title>
            <v-card-text>
              <text-field
                :label="$t('pages.profile.fullName')"
                v-model="model.client.fullName"
                disabled
                required
                :rules="rule.required"
              ></text-field>
              <v-layout row wrap>
                <v-flex xs8 md4 lg4>
                  <text-field
                    v-if="userRole === ROLES.CLIENT_ROLE"
                    :label="$t('pages.profile.username')"
                    required
                    v-model="model.client.document"
                    :mask="['###.###.###-##', '##.###.###/####-##']"
                    disabled
                    :rules="rule.required"
                  ></text-field>
                  <text-field
                    v-else
                    :label="$t('pages.profile.username')"
                    required
                    v-model="model.client.username"
                    :mask="['###.###.###-##', '##.###.###/####-##']"
                    disabled
                    :rules="rule.required"
                  ></text-field>
                </v-flex>
                <v-flex xs8 md4 lg4 class="field-spacing">
                  <text-field
                    v-if="userRole === ROLES.CLIENT_ROLE"
                    :label="$t('pages.profile.document')"
                    required
                    v-model="model.client.identity"
                    disabled
                  ></text-field>
                </v-flex>
              </v-layout>
              {{ $t('pages.profile.personalDataNote') }}
              <a
                v-if="ticketCrmEnabled"
                @click="$router.push({ name: 'ticketForm' })"
                target="__blank"
                class="profile__link"
                >{{ $t('pages.profile.clickHereAppend') }}</a
              >
            </v-card-text>
          </v-card>
          <v-card class="mt-4">
            <v-card-title class="pt-headline profile--color">{{
              $t('pages.profile.contactData')
            }}</v-card-title>
            <v-card-text>
              <text-field
                :label="$t('pages.profile.email')"
                type="email"
                v-model="model.client.email"
                :rules="rule.requiredEmail"
                @input="updateEmail"
                required
              ></text-field>
              <text-field
                v-show="updatingEmail"
                type="email"
                :label="$t('pages.profile.confirmEmail')"
                v-model="confirmEmail"
                :rules="updatingEmail ? confirmEmailRules : []"
                :error="!!error.emails"
                :error-messages="error.emails"
                @input="updateConfirmEmail"
                required
              ></text-field>
              <v-layout row wrap>
                <v-flex xs8 md4 lg4>
                  <text-field
                    type="tel"
                    ref="telephone"
                    :label="$t('pages.profile.phone.telephone')"
                    v-model="profileItem.telephone"
                    :mask="['(##) ####-####', '(##) #####-####']"
                    @blur="validatePhones"
                    :rules="[
                      (v) =>
                        v.length >= 10 ||
                        !v ||
                        $t('pages.profile.phone.invalidPhone'),
                    ]"
                  ></text-field>
                </v-flex>
                <v-flex xs8 md4 lg4 class="field-spacing">
                  <text-field
                    type="tel"
                    ref="cellphone"
                    :label="$t('pages.profile.phone.cellphone')"
                    :rules="[
                      (v) => !!v || $t('labels.requiredField'),
                      (v) =>
                        v.length >= 10 ||
                        !v ||
                        $t('pages.profile.phone.invalidCellphone'),
                    ]"
                    v-model="profileItem.cellphone"
                    required
                    :mask="['(##) ####-####', '(##) #####-####']"
                    @blur="validatePhones"
                  ></text-field>
                </v-flex>
              </v-layout>
              {{ requiredDisclaimer }}
            </v-card-text>
          </v-card>
          <v-card class="mt-4">
            <v-card-title class="pt-headline profile--color">{{
              $t('pages.profile.address.title')
            }}</v-card-title>
            <v-card-text>
              <v-layout row wrap>
                <v-flex xs12 md4 lg4>
                  <text-field
                    :label="$t('pages.profile.address.zipcode')"
                    v-model="model.client.address.zipcode"
                    :mask="['#####-###']"
                    :rules="cepRule"
                    @blur="getAddress"
                    type="tel"
                    required
                  ></text-field>
                </v-flex>
                <v-flex xs12>
                  <text-field
                    :label="$t('pages.profile.address.title')"
                    v-model="model.client.address.address"
                    :rules="rule.required"
                    required
                    maxlength="60"
                  ></text-field>
                </v-flex>
                <v-flex xs12 md4 lg4>
                  <text-field
                    :label="$t('pages.profile.address.number')"
                    v-model="model.client.address.number"
                    :rules="rule.required"
                    required
                    maxlength="20"
                  ></text-field>
                </v-flex>
                <v-flex xs12 md8 lg8 class="field-spacing">
                  <text-field
                    :label="$t('pages.profile.address.complement')"
                    v-model="model.client.address.complement"
                    maxlength="10"
                  ></text-field>
                </v-flex>
                <v-flex xs12>
                  <text-field
                    :label="$t('pages.profile.address.district')"
                    v-model="model.client.address.district"
                    :rules="rule.required"
                    required
                    maxlength="50"
                  ></text-field>
                </v-flex>
                <v-flex xs12 md4 lg4>
                  <select-field
                    :label="$t('pages.profile.address.state')"
                    v-model="model.client.address.stateId"
                    :rules="rule.required"
                    required
                    :loading="statesLoading"
                    :items="states"
                    @change="getCities"
                    selectKeyName="id"
                    selectValueName="code"
                    :placeholder="$t('pages.profile.address.statePlaceholder')"
                    :loadingPlaceholder="
                      $t('pages.profile.address.stateLoading')
                    "
                    :noDataLabel="$t('pages.profile.address.stateNoData')"
                  ></select-field>
                </v-flex>
                <v-flex xs12 md8 lg8 class="field-spacing">
                  <select-field
                    :label="$t('pages.profile.address.city')"
                    v-model="model.client.address.cityId"
                    :rules="rule.required"
                    required
                    :disabled="statesLoading"
                    :loading="citiesLoading"
                    :items="selectedStateCities"
                    :error-messages="error.city"
                    selectKeyName="id"
                    selectValueName="text"
                    :placeholder="$t('pages.profile.address.cityPlaceholder')"
                    :loadingPlaceholder="
                      $t('pages.profile.address.cityLoading')
                    "
                    :noDataLabel="$t('pages.profile.address.cityNoData')"
                    @change="error.city = ''"
                  ></select-field>
                </v-flex>
              </v-layout>
              {{ requiredDisclaimer }}
            </v-card-text>
          </v-card>
          <v-card class="mt-4 mb-0">
            <v-card-title
              ref="currentPasswordAnchor"
              class="pt-headline profile--color"
              >{{ $t('pages.profile.password.changePassword') }}</v-card-title
            >
            <v-card-text>
              <v-layout row wrap>
                <v-flex xs12>
                  <v-flex xs12 md4 lg4>
                    <text-field
                      :label="$t('pages.profile.password.currentPassword')"
                      type="password"
                      :rules="!model.user.oldPassword ? [] : rule.oldPassword"
                      v-model="model.user.oldPassword"
                      :error-messages="error.oldPassword"
                    ></text-field>
                  </v-flex>
                </v-flex>
                <v-flex xs12>
                  <v-flex xs12 md5 lg5>
                    <text-field
                      :label="$t('pages.profile.password.newPassword')"
                      v-model="model.user.newPassword"
                      :rules="!model.user.newPassword ? [] : rule.password"
                      :error="!!error.passwords"
                      :error-messages="error.passwords"
                      type="password"
                    ></text-field>
                  </v-flex>
                </v-flex>
                <v-flex xs12>
                  <v-flex xs12 md5 lg5>
                    <text-field
                      :label="$t('pages.profile.password.confirmPassword')"
                      v-model="model.user.confirmPassword"
                      :rules="!model.user.confirmPassword ? [] : rule.password"
                      :error="!!error.passwords"
                      :error-messages="error.passwords"
                      type="password"
                    ></text-field>
                  </v-flex>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
          <v-btn
            class="profile--background mb-5 mt-3 btn-md"
            type="submit"
            dark
          >
            <v-progress-circular
              color="white"
              indeterminate
              v-if="loadingForm"
            ></v-progress-circular>
            <span v-else>{{ $t('labels.save') }}</span>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-form>
  </module-page>
</template>

<script>
import ModulePage from '@/components/layout/Page';
import ScreenLoading from '@/components/loaders/ScreenLoading';
import SelectField from '@/components/fields/Select';
import TextField from '@/components/fields/TextField';
import encrypt from '../services/encrypt';
import addressService from '../services/address';
import { APP, USER, TICKET_CRM, ROLES } from '../utils/constants';
import profileMixin from '../mixins/modules/profile';
import bus from '../utils/bus';

export default {
  name: 'profile',
  mixins: [profileMixin],
  components: {
    ModulePage,
    ScreenLoading,
    SelectField,
    TextField,
  },
  computed: {
    userRole() {
      return this.$store.getters[USER.GET_USER].userRole;
    },
    hasOnePhone() {
      const { telephone, cellphone } = this.profileItem;
      return telephone.length > 0 || cellphone.length > 0;
    },
    confirmEmailRules() {
      return [
        ...this.rule.requiredEmail,
        (v) => v == this.model.client.email || 'Os e-mails não conferem!',
      ];
    },
    ticketCrmEnabled() {
      return this.$store.getters[TICKET_CRM.GET_ENABLED];
    },
  },
  data() {
    return {
      updatingEmail: false,
      confirmEmail: '',
      requiredDisclaimer: '* Campos obrigatórios',
      cepRule: [
        (v) => !!v || 'Campo de preenchimento obrigatório',
        (v) => v.length >= 8 || 'CEP inválido',
      ],
      pageParams: this.$store.getters[APP.PAGES].profile,
      error: {
        passwords: '',
        city: '',
        emails: '',
      },
      formValid: true,
      loadingForm: false,
      loading: false,
      statesLoading: false,
      citiesLoading: false,
      oldAddress: {
        zipcode: '',
        address: '',
        number: '',
        complement: '',
        district: '',
        city: '',
        stateCode: '',
      },
      states: [],
      selectedStateCities: [],
      model: {
        user: {
          oldPassword: '',
          newPassword: '',
          confirmPassword: '',
        },
        client: {
          id: '',
          email: '',
          telephone: {
            areaCode: '',
            ext: '',
            isActive: true,
            number: '',
            type: 'Residencial',
          },
          cellphone: {
            areaCode: '',
            ext: '',
            isActive: true,
            number: '',
            type: 'Celular',
          },
          address: {
            zipcode: '',
            address: '',
            number: '',
            complement: '',
            district: '',
            cityId: '',
            stateId: '',
          },
        },
      },
    };
  },
  created() {
    this._getUser();
  },
  methods: {
    async getStates() {
      try {
        this.statesLoading = true;
        const countryId = this.enums.locationApiBrazilId;
        const url = `${process.env.LOCATION_API}/getStates`;
        const result = await this.requestService(
          url,
          'GET',
          {
            params: { countryId },
            useLoading: true
          }
        );
        if (result) this.states = result;
      } finally {
        this.statesLoading = false;
      }
    },
    async getCities(stateId) {
      try {
        if (!stateId) return;
        this.citiesLoading = true;
        const url = `${process.env.LOCATION_API}/getCities`;
        const result = await this.requestService(
          url,
          'GET',
          {
            params: { stateId },
            useLoading: false
          }
        );
        if (result) this.selectedStateCities = result;
      } finally {
        this.citiesLoading = false;
      }
    },
    async getAddress() {
      const { stateCode, city, ...address } = await addressService(
        this.model.client.address.zipcode
      );
      const stateId = this.states.find((o) => o.code === stateCode).id;
      await this.getCities(stateId);
      const cityId = this.selectedStateCities.find((o) => o.text === city).id;

      this.model.client.address = {
        ...this.model.client.address,
        ...address,
        stateId,
        cityId,
      };
    },
    async submit() {
      try {
        if (
          !this.$refs.profileForm.validate() ||
          !this.passwordsCheck() ||
          !this.emailsCheck() ||
          !this.cityStateCheck()
        ) {
          const [errorKey] = Object.entries(
            this.$refs.profileForm.errorBag
          ).find(([key, value]) => value);
          if (errorKey) {
            const scrollTarget = this.$refs.profileForm.inputs.find(
              (field) => field._uid == errorKey
            );
            scrollTarget.$el.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
          return;
        }
        this.loadingForm = true;

        let { oldPassword, newPassword } = this.model.user;
        if (oldPassword) oldPassword = encrypt(oldPassword);
        if (newPassword) newPassword = encrypt(newPassword);

        const clientRaw = Object.assign(
          {},
          this.$store.getters[USER.GET_IMOBME_DATA]
        );

        const {
          lastUpdate,
          fullName,
          clientCode,
          document,
          birthday,
          identity,
          stateInscription,
          municipalInscription,
          ...client
        } = Object.assign({}, this.model.client);

        const oldEmail = clientRaw.email;
        const newEmail = client.email;

        const clientRawAddress = Object.assign({}, this.oldAddress);
        const clientRawTelephone = Object.assign({}, clientRaw.telephone);
        const clientRawCellphone = Object.assign({}, clientRaw.cellphone);

        const user = this.$store.getters[USER.GET_USER];
        const isClient = user.userRole === ROLES.CLIENT_ROLE;
        let body = {
          userRole: user.userRole,
          user: {
            _id: user._id,
            email: newEmail,
            oldPassword,
            newPassword,
          },
          ticketCrmToken: this.$store.getters[TICKET_CRM.GET_TOKEN],
        };

        if (isClient) {
          body.client = client;
        } else {
          body.user.telephone = user.telephone;
          body.user.cellphone = user.cellphone;
          body.user.address = this.model.client.address;
        }

        const telephone = this.formatPhoneStringToObject(
          this.profileItem.telephone
        );
        const cellphone = this.formatPhoneStringToObject(
          this.profileItem.cellphone
        );

        const phoneUser = isClient ? 'client' : 'user';

        if (Object.keys(telephone).length == 0) {
          body[phoneUser].telephone.isActive = false;
        }
        if (Object.keys(telephone).length > 0) {
          body[phoneUser].telephone.number = telephone.number;
          body[phoneUser].telephone.areaCode = telephone.areaCode;
        }
        if (Object.keys(cellphone).length == 0) {
          body[phoneUser].cellphone.isActive = false;
        }
        if (Object.keys(cellphone).length > 0) {
          body[phoneUser].cellphone.number = cellphone.number;
          body[phoneUser].cellphone.areaCode = cellphone.areaCode;
        }

        if (!isClient) {
          const { user: userData, ...bodyRest } = body;
          body = { ...bodyRest, ...userData };
        }

        const url = `${process.env.AUTH_API_URL}/user/updateUser`;
        const result = await this.requestService(url, 'POST',
        {
          data: body,
          localErrorHandling: true
        });

        if (result) {
          const changedData = {};
          const contactData = this.getContactUpdatedData({
            cellphone,
            clientRawCellphone,
            telephone,
            clientRawTelephone,
            oldEmail,
            newEmail,
          });
          const addressData = this.getAddressUpdatedData({
            oldAddress: clientRawAddress,
            newAddress: client.address,
          });

          if (contactData) changedData.contact = contactData;
          if (addressData) changedData.address = addressData;

          if (Object.keys(changedData).length > 0)
            this.createTrace(
              this.enums.logTraceActions.profileChange,
              changedData
            );

          if (oldPassword && newPassword) {
            this.createTrace(this.enums.logTraceActions.passwordChange);
          }

          bus.$emit('snackbar:open', {
            text: 'Cadastro atualizado com sucesso!',
            color: 'green',
          });

          this.$store.commit(USER.MUTATE_IMOBME_DATA, body.client);

          this.updatingEmail = false;
          this.confirmEmail = '';
        }
      } catch (err) {
        const errorFields = this.handleAuthError(err);
        if (errorFields) {
          this.error = errorFields;
        }
      } finally {
        this.loadingForm = false;
      }
    },
    async _getUser() {
      try {
        this.loading = true;

        await Promise.all([this.getUser(), this.getStates()]);

        const data = this.$store.getters[USER.GET_IMOBME_DATA];

        await this.getCities(data.address.stateId);

        this.oldAddress = Object.assign({}, data.address);
        this.model.client = Object.assign(this.model.client, data);

        const { zipcode } = this.model.client.address;
        if (zipcode) {
          const parsedZipcode = zipcode
            .slice(0, 5)
            .concat('-', zipcode.slice(5, zipcode.length));
          this.model.client.address.zipcode = parsedZipcode;
        }

        this.checkPasswordScreen();
      } finally {
        this.loading = false;
      }
    },
    passwordsCheck() {
      if (this.model.user.newPassword == this.model.user.confirmPassword)
        return true;
      this.error.passwords = 'As senhas não conferem!';
      return false;
    },
    emailsCheck() {
      if (!this.updatingEmail || this.model.client.email == this.confirmEmail)
        return true;
      this.error.emails = 'Os e-mails não conferem!';
      return false;
    },
    cityStateCheck() {
      if (
        this.selectedStateCities.find(
          (o) => o.id === this.model.client.address.cityId
        ) !== undefined
      ) {
        return true;
      }
      this.error.city = 'Selecione uma cidade!';
      return false;
    },
    checkPasswordScreen() {
      if (
        this.$router.currentRoute.name === 'profilePassword' &&
        this.$refs.currentPasswordAnchor
      ) {
        const element = this.$refs.currentPasswordAnchor;
        const top =
          element.getBoundingClientRect().top +
          document.documentElement.scrollTop;
        window.scrollTo(0, top);
      } else {
        window.scrollTo(0, 0);
      }
    },
    updateEmail(v) {
      if (!this.updatingEmail) this.updatingEmail = true;
      v = v.toLowerCase();
      this.profileItem.email = v;
      this.model.user.email = v;
      this.model.client.email = v;
    },
    updateConfirmEmail(v) {
      v = v.toLowerCase();
      this.confirmEmail = v;
      this.error.emails = '';
    },
    validatePhones() {
      this.$refs['telephone'].validate();
      this.$refs['cellphone'].validate();
    },
  },
  watch: {
    $route() {
      this.checkPasswordScreen();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../assets/scss/breakpoint';
>>> .v-card__title {
  padding-bottom: 0px;
}

.profile {
  &__link {
    color: var(--profile-color);
    text-decoration: underline;
  }
}

.field-spacing {
  @include breakpoint(not-mobile) {
    padding-left: 8px !important;
  }

  @include breakpoint(mobile) {
    padding-left: 0px !important;
  }
}
</style>



// WEBPACK FOOTER //
// ./src/views/Profile.vue